import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, styled } from '@mui/material/styles';
import type { RoadmapNode } from "../../api/roadmap-service";

import Iconify from "../iconify";

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 120,
    maxWidth: 240,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    whiteSpace: 'normal',
}));

/** Each depth of the map gets its own weight, so the eye finds the top branches first; lessons are the lightest. */
function nodeStyle(theme: Theme, node: RoadmapNode, depth: number) {
    if (node.kind === 'LESSON') return { bgcolor: theme.palette.background.paper, typography: 'caption' };
    if (depth === 0) {
        return { bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, typography: 'h6' };
    }
    if (depth === 1) return { bgcolor: alpha(theme.palette.primary.main, 0.16), typography: 'subtitle1' };
    if (depth === 2) return { bgcolor: alpha(theme.palette.info.main, 0.16), typography: 'subtitle2' };

    return { bgcolor: alpha(theme.palette.grey[500], 0.12), typography: 'body2' };
}

export type RoadmapNodeBoxProps = {
    node: RoadmapNode;
    /** Distance from the roadmap itself, which is 0. */
    depth: number;
    levelName: string;
    collapsed: boolean;
    onToggle: () => void;
    onOpen?: () => void;
    onNotes?: () => void;
};

/** One node of the mindmap; a planned node is drawn dashed and muted, because the catalogue does not have it yet. */
export function RoadmapNodeBox({ node, depth, levelName, collapsed, onToggle, onOpen, onNotes }: RoadmapNodeBoxProps) {
    const hasChildren = node.children.length > 0;
    const tooltip = node.planned ? `Planned · from ${ levelName }` : `From ${ levelName }`;

    return <StyledBox
        data-testid="roadmap-node"
        sx={ (theme) => ({
            ...nodeStyle(theme, node, depth),
            borderColor: node.planned ? theme.palette.grey[500] : 'transparent',
            borderStyle: node.planned ? 'dashed' : 'solid',
            opacity: node.planned ? 0.65 : 1,
            boxShadow: node.kind === 'LESSON' && !node.planned ? theme.customShadows.z1 : 'none',
        }) }
    >
        <Tooltip title={ tooltip }>
            <Typography
                component="span"
                variant="inherit"
                onClick={ onOpen }
                sx={ { flexGrow: 1, cursor: onOpen ? 'pointer' : 'default', '&:hover': onOpen ? { textDecoration: 'underline' } : {} } }
            >
                { node.title }
            </Typography>
        </Tooltip>

        { onNotes && (
            <Tooltip title={ `Required knowledge · ${ node.notes.length }` }>
                <IconButton size="small" color="inherit" onClick={ onNotes } aria-label="Required knowledge"
                            data-testid="roadmap-notes">
                    <Iconify icon="eva:file-text-outline" sx={ { width: 16, height: 16 } }/>
                </IconButton>
            </Tooltip>
        ) }

        { hasChildren && (
            <IconButton size="small" color="inherit" onClick={ onToggle } aria-label={ collapsed ? 'Expand' : 'Collapse' }>
                <Iconify icon={ collapsed ? 'eva:plus-circle-outline' : 'eva:minus-circle-outline' } sx={ { width: 16, height: 16 } }/>
            </IconButton>
        ) }
    </StyledBox>;
}
